var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { InputType } from '@philips-internal/oswald-ui-constants';
import { AwesomeEvent, ComponentAPI } from '@philips-internal/oswald-ui-core';
import { ChangeEventListener, FocusEventListener, KeyboardEventListener } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { isEnumValid } from '@philips-internal/oswald-ui-utils';
class InputAPI extends ComponentAPI {
    constructor() {
        super(...arguments);
        /** @event */
        this.onChange = new ChangeEventListener();
        /** @event */
        this.onBlur = new FocusEventListener();
        /** @event */
        this.onFocus = new FocusEventListener();
        /** @event */
        this.onKeyDown = new KeyboardEventListener();
        /** @event */
        this.onKeyPress = new KeyboardEventListener();
        /** @event */
        this.onKeyUp = new KeyboardEventListener();
    }
    /**
     * Returns if the input will auto capitalize the value.
     */
    isAutoCapitalize() {
        return this.state.autoCapitalize;
    }
    /**
     * Sets if the input should auto capitalize the value.
     *
     * @param autoCapitalize - If the input should auto capitalize the value.
     */
    setAutoCapitalize(autoCapitalize) {
        this.setState({ autoCapitalize });
    }
    /**
     * Returns the autocomplete property added to the native input.
     */
    isAutoComplete() {
        return this.state.autoComplete;
    }
    /**
     * Sets the auto complete property of the native input.
     *
     * @param autoComplete - The auto complete property value.
     */
    setAutoComplete(autoComplete) {
        this.setState({ autoComplete });
    }
    /**
     * Returns if the auto correct property is added to the native input.
     */
    isAutoCorrect() {
        return Boolean(this.state.autoCorrect);
    }
    /**
     * Sets if the auto correct property of the native input should be added.
     *
     * @param autoCorrect - If the auto correct property of the native input should be added.
     */
    setAutoCorrect(autoCorrect) {
        this.setState({ autoCorrect });
    }
    /**
     * Returns if the input will generate ellipsis on overflow.
     */
    isEllipsis() {
        return Boolean(this.state.ellipsis);
    }
    /**
    * Sets if the input will generate ellipsis on overflow.
    *
    * @param ellipsis - If the input will generate ellipsis on overflow.
    */
    setEllipsis(ellipsis) {
        this.setState({ ellipsis });
    }
    /**
     * Returns if the input is enabled.
     */
    isEnabled() {
        return this.state.enabled;
    }
    /**
     * Sets if the input is enabled.
     *
     * @param enabled - If the input is enabled.
     */
    setEnabled(enabled) {
        this.setState({ enabled });
    }
    /**
     * Returns if the input is limiting the value length.
     */
    isEnforceLength() {
        return this.state.enforceLength;
    }
    /**
     * Sets if the input should limit the value length.
     *
     * @param enforceLength - If the input should limit the value length.
     */
    setEnforceLength(enforceLength) {
        this.setState({ enforceLength });
    }
    /**
     * Returns the input mode of the element.
     */
    getInputMode() {
        return this.state.inputMode;
    }
    /**
     * Sets the input mode for the element.
     *
     * @param inputMode - The input mode.
     */
    setInputMode(inputMode) {
        this.setState({ inputMode });
    }
    /**
     * Returns the max length of the input value.
     */
    getLength() {
        return this.state.length;
    }
    /**
     * Sets if the input should limit the value length.
     *
     * @param length - If the input should limit the value length.
     */
    setLength(length) {
        this.setState({ length });
    }
    /**
     * Returns the max value for input.
     */
    getMax() {
        return this.state.max;
    }
    /**
     * Sets the max value of the input.
     *
     * @param max - The max value of the input.
     */
    setMax(max) {
        this.setState({ max });
    }
    /**
     * Returns the min value for input.
     */
    getMin() {
        return this.state.min;
    }
    /**
     * Sets the min value of the input.
     *
     * @param min - The min value of the input.
     */
    setMin(min) {
        this.setState({ min });
    }
    /**
     * Returns the name of the input.
     */
    getName() {
        return this.state.name;
    }
    /**
     * Sets the name of the input.
     *
     * @param name - The name of the input.
     */
    setName(name) {
        this.setState({ name });
    }
    /**
     * Returns the placeholder of the input.
     */
    getPlaceholder() {
        return this.state.placeholder;
    }
    /**
     * Sets the placeholder of the input.
     *
     * @param placeholder - The placeholder of the input.
     */
    setPlaceholder(placeholder) {
        this.setState({ placeholder });
    }
    /**
     * Returns if the input is read only.
     */
    isReadOnly() {
        return Boolean(this.state.readOnly);
    }
    /**
     * Sets if the input should be read only.
     *
     * @param readOnly - If the input should be read only.
     */
    setReadOnly(readOnly) {
        this.setState({ readOnly });
    }
    /**
     * Returns if the input is showing the value length.
     */
    isShowLength() {
        return this.state.showLength;
    }
    /**
     * Sets if the input should show the value length.
     *
     * @param showLength - If the input should show the value length.
     */
    setShowLength(showLength) {
        this.setState({ showLength });
    }
    /**
     * Returns if the input is showing the password toggle.
     */
    isShowPasswordToggle() {
        return this.state.showPasswordToggle;
    }
    /**
     * Sets if the input should show the password toggle.
     *
     * @param showPasswordToggle - If the input should show the password toggle.
     */
    setShowPasswordToggle(showPasswordToggle) {
        this.setState({ showPasswordToggle });
    }
    /**
     * Returns the step of increment/decrement for the input.
     */
    getStep() {
        return this.state.step;
    }
    /**
     * Sets the step of increment/decrement for the input.
     *
     * @param step - The step of increment/decrement for the input.
     */
    setStep(step) {
        this.setState({ step });
    }
    /**
     * Returns the type of the input.
     */
    getType() {
        return this.state.type;
    }
    /**
     * Sets the type of the input.
     *
     * @param type - The type of the input.
     */
    setType(type) {
        if (isEnumValid(InputType, type)) {
            this.setState({ type });
        }
    }
    /**
     * Returns the value of the input.
     */
    getValue() {
        return this.state.value;
    }
    /**
     * Sets the value of the input.
     *
     * @param value - The value.
     */
    setValue(value) {
        const { value: oldValue } = this.state;
        this.setState({ value });
        this.onChange.fire({ oldValue, value });
    }
    /**
     * Sets if the input should be focused.
     *
     * @param focused - If the input should be focused.
     */
    setFocus(focused) {
        this.setState({ focused });
    }
    /**
     * Returns if the input should be auto focused.
     */
    isAutoFocus() {
        return Boolean(this.state.autoFocus);
    }
    /**
     * Sets if the input should be auto focused.
     *
     * @param autoFocus - If the input should be auto focused.
     */
    setAutoFocus(autoFocus) {
        this.setState({ autoFocus });
    }
    /**
     * Returns the configured external action element.
     */
    getExternalAction() {
        return this.state.externalAction;
    }
    /**
     * Sets the configured external action element.
     *
     * @param externalAction - The external action element.
     */
    setExternalAction(externalAction) {
        this.setState({ externalAction });
    }
    /**
     * Returns if the input validations are being suppressed.
     */
    isSuppressValidations() {
        return Boolean(this.state.suppressValidations);
    }
    /**
     * Sets if the input should not interact with the `Field` component context.
     *
     * @param suppressValidations - If the input validations should be suppressed.
     */
    setSuppressValidations(suppressValidations) {
        this.setState({ suppressValidations });
    }
}
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], InputAPI.prototype, "onChange", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], InputAPI.prototype, "onBlur", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], InputAPI.prototype, "onFocus", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], InputAPI.prototype, "onKeyDown", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], InputAPI.prototype, "onKeyPress", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], InputAPI.prototype, "onKeyUp", void 0);
export { InputAPI, };
//# sourceMappingURL=input-api.js.map